import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { displayName } from '@promanage/contracts/person-display';
import { formatRef, isRunning, parseRef, spanState } from '@promanage/core';
import { Button } from '@promanage/ui';
import { ColumnPicker } from '../features/table/ColumnPicker';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type SortState } from '../features/table/columns';
import { useColumnState } from '../features/table/useColumnState';
import {
  DEFAULT_TENANCY_SORT,
  tenancyColumns,
  type TenancyColumnId,
} from '../features/property/tenancyColumns';
import { todayIso } from '../features/property/today';
import type { TenancyOption } from '../features/property/types';
import { trpc } from '../lib/trpc';
import { usePortfolioScope } from '../features/portfolio/scope';
import { TenanciesNav } from '../features/arrears/TenanciesNav';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import { workspaceChoice, workspaceTable, workspaceText } from '../features/workspaces/state';

export const Route = createFileRoute('/tenancies/')({
  component: TenanciesPage,
});

const STORAGE_KEY = 'promanage:tenancy-columns';

/** One identity, so the pending state does not re-sort the table on every render. */
const NO_TENANCIES: TenancyOption[] = [];

type StateFilter = 'all' | 'active' | 'upcoming' | 'ending' | 'ended' | 'unattached';

const FILTERS: { value: StateFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'upcoming', label: 'Upcoming' },
  // The working list. A tenancy with a leaving date set needs a final inspection, a bond
  // refund and a re-let, and all three are cheaper in the weeks before than the morning after.
  { value: 'ending', label: 'Ending' },
  { value: 'ended', label: 'Ended' },
  { value: 'unattached', label: 'Nobody attached' },
];

/**
 * The tenancies list.
 *
 * A tenancy is a LETTING — one row per letting, not per property and not per tenant. The same
 * address appears once for each tenancy it has had, which is the whole reason this page exists
 * separately from Properties: "who was in 12 Kowhai St in March" is a different row from
 * "who is in it now", and the property page can only show you one place at a time.
 *
 * Ended tenancies are listed by default rather than hidden behind a toggle. A finished tenancy
 * is not clutter — it is what a bond dispute, an arrears claim or a damage claim is about, and
 * those arrive months after the keys came back.
 *
 * "Nobody attached" is a filter because it is a real backlog: a tenancy with no tenant on it
 * is a tenancy nobody can be served notice about.
 */
function TenanciesPage() {
  const navigate = useNavigate();
  const today = todayIso();
  const { scope, setScope, canToggle } = usePortfolioScope();
  const list = useQuery(
    trpc.property.tenancies.queryOptions(
      { scope },
      { meta: { errorTitle: 'Could not load the tenancies' }, staleTime: 10_000 },
    ),
  );
  const tenancies = list.data ?? NO_TENANCIES;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StateFilter>('all');
  const [sort, setSort] = useState<SortState<TenancyColumnId>>(DEFAULT_TENANCY_SORT);

  const catalogue = useMemo(() => tenancyColumns(today), [today]);
  const columns = useColumnState(STORAGE_KEY, catalogue);
  const workspace = useWorkspace({
    route: '/tenancies',
    state: { filters: { query, filter, scope }, columns: columns.state.order, sort },
    apply(state) {
      const table = workspaceTable(state, catalogue, DEFAULT_TENANCY_SORT);
      setQuery(workspaceText(state, 'query'));
      setFilter(
        workspaceChoice(
          state,
          'filter',
          FILTERS.map((option) => option.value),
          'all',
        ),
      );
      if (canToggle) setScope(workspaceChoice(state, 'scope', ['own', 'agency'], 'own'));
      columns.setState(table.columns);
      setSort(table.sort);
    },
  });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tenancies
      .filter((tenancy) => {
        if (filter === 'unattached') return tenancy.people.length === 0;
        if (filter === 'all') return true;
        return spanState(tenancy, today) === filter;
      })
      .filter((tenancy) => {
        if (!q) return true;

        // A tenancy ref finds the tenancy; a property ref finds every tenancy that property
        // has had, which is the more useful answer than "no results".
        const asRef = parseRef(q);
        if (asRef?.entity === 'tenancy') return asRef.sequence === tenancy.refSeq;
        if (asRef?.entity === 'property') return asRef.sequence === tenancy.propertyRefSeq;

        return [
          tenancy.address,
          formatRef('tenancy', tenancy.refSeq),
          tenancy.suburb,
          tenancy.city,
          ...tenancy.people.map(displayName),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => compareRows(a, b, sort, catalogue));
  }, [tenancies, query, filter, today, sort, catalogue]);

  const total = tenancies.length;
  // Running counts both `active` and `ending` — somebody is living there either way, and a
  // count that dropped when a tenant gave notice would read as a tenancy already gone.
  const running = tenancies.filter((t) => isRunning(t, today)).length;
  const ending = tenancies.filter((t) => spanState(t, today) === 'ending').length;
  const summary = list.isPending
    ? 'Loading…'
    : rows.length === total
      ? `${total} tenancies · ${running} running${ending > 0 ? ` · ${ending} ending` : ''}`
      : `Showing ${rows.length} of ${total} tenancies`;

  return (
    <ListShell
      title="Tenancies"
      subtitle={summary}
      actions={
        <>
          <WorkspaceMenu workspace={workspace} />
          <ColumnPicker
            columns={catalogue}
            state={columns.state}
            onChange={columns.setState}
            onReset={columns.reset}
          />
          <Link to="/tenancies/new">
            <Button size="sm">
              <span aria-hidden="true">+</span> Add tenancy
            </Button>
          </Link>
        </>
      }
      search={query}
      onSearchChange={setQuery}
      nav={<TenanciesNav />}
      searchPlaceholder="Search address, ref (TE000001) or tenant"
      searchLabel="Search tenancies"
      filters={
        <FilterChips
          label="Filter by state"
          value={filter}
          options={FILTERS}
          onChange={setFilter}
        />
      }
      isPending={list.isPending}
      error={list.error}
      onRetry={() => void list.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState
          title={query ? `Nothing matches “${query}”` : 'No tenancies yet'}
          action={
            <Link to="/tenancies/new">
              <Button size="sm">Add tenancy</Button>
            </Link>
          }
        >
          {query
            ? 'Try a different address, ref or tenant name.'
            : 'A tenancy is a letting of a property. Starting one opens its ledger account, where rent is credited and arrears show.'}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={columns.visible}
        sort={sort}
        onSortChange={setSort}
        rowKey={(tenancy) => tenancy.id}
        onOpen={(tenancy) =>
          void navigate({
            to: '/tenancies/$ref',
            params: { ref: formatRef('tenancy', tenancy.refSeq) },
          })
        }
        caption={`Tenancies, one row per letting. ${summary}.`}
        minWidth="56rem"
      />
    </ListShell>
  );
}
